import { useCallback } from "react";
import { Box, Button, Fade, Flex, Heading, Text } from "@chakra-ui/react";
import { getLayout } from "components/Layout";
import PageSection from "components/Layout/PageSection";
import SelectSkuMenu from "components/Shared/Select/SelectSkuMenu";
import HeadTitle from "components/Shared/HeadTitle";
import {
  useClearRouteParamsOnBrandChange,
  useRouteParam,
} from "utils/routeParamHooks";
import SkuPricingCalculator from "components/Pricing/SkuPricingCalculator";
import {
  usePricingCalculatorForm,
  useUpdatePricingCalculation,
} from "components/Pricing/pricingCalculatorUtils";
import asyncVoidHandler from "utils/asyncVoidHandler";

const PAGE_TITLE = "Pricing Calculator";

export default function PricingPage() {
  useClearRouteParamsOnBrandChange(["skuId"]);
  const [skuId, navigateToSku] = useRouteParam("skuId");

  const formContext = usePricingCalculatorForm();
  const {
    formState: { isDirty },
    reset,
  } = formContext;

  const [handleSubmit, { loading: isSaving }] =
    useUpdatePricingCalculation(skuId);

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  return (
    <PageSection maxWidth="80rem">
      <HeadTitle title={PAGE_TITLE} />
      <Heading as="h1" variant="section-header" marginBottom="2rem">
        {PAGE_TITLE}
      </Heading>

      <Text fontSize="sm" color="gray" my={2}>
        To price an existing product and save your calculations, select a SKU.
        Otherwise, start by specifying unit COGS manually.
      </Text>

      <Box
        as="form"
        onSubmit={asyncVoidHandler(formContext.handleSubmit(handleSubmit))}
        noValidate
      >
        <Flex alignItems="center" flexWrap="wrap">
          <Box flex="1 0 0" minWidth="12em" maxWidth="40em" my={2}>
            <SelectSkuMenu
              value={skuId || null}
              onChange={asyncVoidHandler(navigateToSku)}
              valueQueryOptions={{ fetchPolicy: "cache-only" }}
              isClearable
              backspaceRemovesValue
              id="SelectSkuMenu-sku-drop-down"
              instanceId="select-sku"
            />
          </Box>
          <Flex ml="auto" flexWrap="wrap" justifyContent="flex-end" my={2}>
            <Fade in={isDirty}>
              <Button
                type="button"
                variant="ghost"
                color="gray.400"
                px={6}
                mx={4}
                onClick={handleReset}
              >
                Reset
              </Button>
            </Fade>
            <Button
              type="submit"
              colorScheme="lightPinkButton"
              disabled={!isDirty}
              isLoading={isSaving}
              visibility={skuId ? undefined : "hidden"}
            >
              Save
            </Button>
          </Flex>
        </Flex>
        <SkuPricingCalculator formContext={formContext} skuId={skuId} />
      </Box>
    </PageSection>
  );
}

PricingPage.getLayout = getLayout;
